import React from 'react';
import { Box, TextField, Typography, Autocomplete, IconButton, Tooltip, Popover, Button, Divider, Paper, Slider, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { Edit, Plus } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import Dropdown, { DropdownOption } from '../../ui/dropdown';
import { InvoiceData } from '../add-invoice';
import AddClientPopup, { Client } from '../add-client';

import buildApiRequestConfig from '../../../api/url-builder';
import routes from '../../../api/routes';
import useApi from '../../../hooks/use-api';
import { useSettings } from '../../../providers/settings-provider';
import { useToast } from '../../../providers/toaster-provider';
import { usePermissions } from '../../../providers/permissions-provider';
import { formatInvoiceStatusLabel } from '../../../util/xero-invoice-status';

interface InvoiceHeaderProps {
    data: InvoiceData;
    onChange: (updates: Partial<InvoiceData>) => void;
    onStatusChange?: (statusId: string) => void;
    onEditClientClick?: () => void;
    entityId?: number;
    nextInvoiceNumber: string;
    isEdit?: boolean;
    createdDate?: string;
    quotedDate?: string;
    acceptedDate?: string;
    lastEmailDate?: string;
    isStatusEditable?: boolean;
    errors?: { [key: string]: string };
    paymentBehavior?: any;
    provisionDateOffset?: number;
    isMobile?: boolean;
}

const InvoiceHeader: React.FC<InvoiceHeaderProps> = ({ data, onChange, onStatusChange, onEditClientClick, entityId, nextInvoiceNumber, isEdit, createdDate, quotedDate, acceptedDate, lastEmailDate, isStatusEditable = true, errors = {}, paymentBehavior, provisionDateOffset, isMobile = false }) => {
    const { formatDate, formatAmount, companySettings } = useSettings();
    const { showError, showSuccess } = useToast();
    const { can } = usePermissions();
    const [statusOptions, setStatusOptions] = React.useState<DropdownOption[]>([]);
    const [pickerValue, setPickerValue] = React.useState<Dayjs | null>(data.issuedDate ? dayjs(data.issuedDate) : null);
    const [provisionAnchorEl, setProvisionAnchorEl] = React.useState<HTMLElement | null>(null);
    const [draftDate, setDraftDate] = React.useState<Dayjs | null>(null);
    const [draftConfidenceLevel, setDraftConfidenceLevel] = React.useState<number>(100);
    const [provisionDateMode, setProvisionDateMode] = React.useState<'recommended' | 'custom'>('recommended');
    const [draftCustomProvisionDate, setDraftCustomProvisionDate] = React.useState<Dayjs | null>(null);
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    const [openAddClient, setOpenAddClient] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [clientList, setClientList] = React.useState<{
        id: number;
        abbr: string;
        company: string;
        recipient: string;
        streetAddressLine1: string;
        streetAddressLine2: string;
        suburb: string;
        state: string;
        postalCode: string;
        country: string;
        phone: string;
        fax: string;
        email: string;
        provisionDateOffset: number | null;
        isProvisionDateManual: boolean;
        nextInvoiceNumber: string;
    }[]>([]);
    const [clients, setClients] = useApi();
    const [fetchStatus, setFetchStatus] = useApi();
    const [createClientApi, setCreateClientApi] = useApi();

    React.useEffect(() => {
        if (!entityId) return;
        const { url, method } = buildApiRequestConfig(routes.client.fetchByEntity, [{ param: 'entityId', value: entityId }]);
        setClients({ url, method });
    }, [entityId, setClients]);

    React.useEffect(() => {
        if (!entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoiceSettings.fetchStatusesByEntity, [{ param: 'entityId', value: entityId }]);
        setFetchStatus({ url, method });
    }, [entityId, setFetchStatus]);

    React.useEffect(() => {
        const { data, isLoading } = clients;
        if (data && !isLoading) {
            setClientList(data.map((client: any) => ({
                id: client.id,
                abbr: client.abbr,
                company: client.companyName,
                recipient: client.recipient,
                streetAddressLine1: client.address1,
                streetAddressLine2: client.address2,
                suburb: client.suburb,
                state: client.state,
                postalCode: client.postalCode,
                country: client.country,
                phone: client.phone,
                fax: client.fax,
                email: client.email,
                provisionDateOffset: client.provisionDateOffset,
                isProvisionDateManual: !!client.isProvisionDateManual,
                nextInvoiceNumber: client.nextInvoiceNumber || nextInvoiceNumber
            })));
        }
    }, [clients, nextInvoiceNumber]);

    React.useEffect(() => {
        const { data, isLoading } = fetchStatus;
        if (data && !isLoading) {
            const customStatuses = data
                .map((status: any) => ({
                    label: formatInvoiceStatusLabel(status.name),
                    value: String(status.id)
                }));
            
            const allStatuses = [...customStatuses];
            setStatusOptions(allStatuses);
        }
    }, [fetchStatus]);

    const handleCreateClient = (clientData: Client) => {
        const { url, method } = buildApiRequestConfig(routes.client.create);
        setCreateClientApi({
            url,
            method,
            body: {
                entityId,
                abbr: clientData.companyAbbreviation,
                companyName: clientData.company,
                recipient: clientData.recipient,
                address1: clientData.streetAddress1,
                address2: clientData.streetAddress2,
                suburb: clientData.suburb,
                state: clientData.state,
                postalCode: clientData.postalCode,
                country: clientData.country,
                phone: clientData.phone,
                fax: clientData.fax,
                email: clientData.email,
                nextInvoiceNumber: clientData.nextInvoiceNumber,
            }
        });
    };

    React.useEffect(() => {
        const { data: created, isLoading, error } = createClientApi;
        if (created && !isLoading) {
            const newClient = {
                id: created.id,
                abbr: created.abbr,
                company: created.companyName,
                recipient: created.recipient,
                streetAddressLine1: created.address1,
                streetAddressLine2: created.address2 || '',
                suburb: created.suburb,
                state: created.state,
                postalCode: created.postalCode,
                country: created.country,
                phone: created.phone || '',
                fax: created.fax || '',
                email: created.email || '',
                provisionDateOffset: created.provisionDateOffset ?? null,
                isProvisionDateManual: !!created.isProvisionDateManual,
                nextInvoiceNumber: created.nextInvoiceNumber || nextInvoiceNumber,
            };
            setClientList(prev => [...prev, newClient]);
            onChange({
                clientId: String(created.id),
                invoiceNumber: `${created.abbr}${created.nextInvoiceNumber}`,
                companyName: created.companyName,
                recipientName: created.recipient,
                companyAddress: created.address1,
                companyAddress2: created.address2 || '',
                companySuburb: created.suburb,
                companyState: created.state,
                companyPostalCode: created.postalCode,
                companyPhone: created.phone || '',
                companyFax: created.fax || '',
                companyEmail: created.email || '',
            });
            showSuccess('Client added successfully');
            setOpenAddClient(false);
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to create client');
        }
    }, [createClientApi]);

    const isQuote = fetchStatus.data?.find((s: any) => String(s.id) === String(data.status))?.systemName === 'default_quote';

    // The selected client's own record - carries a manually-set provision
    // date offset (Client Details > Provision Date Offset) if an admin ever
    // overrode it there. That override is a deliberate human decision, so it
    // takes priority even over this client's own live payment-history
    // average below.
    const selectedClient = clientList.find(c => String(c.id) === String(data.clientId));
    const hasManualClientOffset = !!(selectedClient?.isProvisionDateManual && selectedClient?.provisionDateOffset != null);

    const computeDefaultProvisionOffset = () => {
        if (hasManualClientOffset) return selectedClient!.provisionDateOffset as number;
        if (paymentBehavior?.averageDaysToPayment != null) return Math.round(paymentBehavior.averageDaysToPayment);
        if (provisionDateOffset && provisionDateOffset > 0) return provisionDateOffset;
        return 7;
    };

    // Resets the Recommended/Custom provision date draft state to match what's
    // currently persisted on the invoice (used when the popover opens and on Clear)
    const resetProvisionDateDraft = (referenceDate: Dayjs) => {
        if (data.provisionDateOffset != null) {
            setProvisionDateMode('custom');
            setDraftCustomProvisionDate(referenceDate.add(data.provisionDateOffset, 'day'));
        } else {
            setProvisionDateMode('recommended');
            setDraftCustomProvisionDate(null);
        }
    };

    const canAddClient = can('invoices.addClient', 'create');
    const CustomPaper = React.useCallback(({ children, ...props }: any) => (
        <Paper {...props}>
            {children}
            {canAddClient && (
                <Box
                    sx={{ borderTop: '1px solid #eee', px: 1.5, py: 0.75 }}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                >
                    <Button
                        size="small"
                        startIcon={<Plus size={13} />}
                        onClick={() => setOpenAddClient(true)}
                        sx={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: 12, textTransform: 'none' }}
                    >
                        Add New Client
                    </Button>
                </Box>
            )}
        </Paper>
    ), [canAddClient]);

    return (
        <>
        <Box>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                <Box sx={{ flex: 1, width: isMobile ? '100%' : undefined }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 20 }}>
                            {isEdit ? 'Edit Invoice' : 'Add Invoice'}
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1} alignItems="flex-start" flexDirection="column">
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={4}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text-muted)' }}>Status:</Typography>
                                <Dropdown
                                    value={data.status}
                                    onChange={(value) => {
                                        onChange({ status: value });
                                        onStatusChange?.(value);
                                    }}
                                    height={32}
                                    width={140}
                                    isPrimary
                                    borderRadius={1}
                                    options={statusOptions}
                                    disabled={isEdit && !isStatusEditable}
                                />
                            </Box>
                            <Tooltip
                                title="Internal Invoice ID"
                                placement="top"
                                arrow
                            >
                                <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'var(--color-primary)' }}>
                                    I.ID: {nextInvoiceNumber}
                                </Typography>
                            </Tooltip>
                            {data.invoiceNumber &&
                                <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'var(--color-primary)' }}>
                                    ID: {data.invoiceNumber}
                                </Typography>
                            }
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Created:</Typography>
                                <Box
                                    sx={{ 
                                        fontSize: 12,
                                        borderRadius: 1,
                                        background: '#eee',
                                        color: 'var(--color-default-text)',
                                        px: 1,
                                        py: 0.5
                                    }}
                                >
                                    {!isEdit ? formatDate?.(new Date()) : createdDate}
                                </Box>
                            </Box>
                            {quotedDate && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Quoted:</Typography>
                                    <Box
                                        sx={{ 
                                            fontSize: 12,
                                            borderRadius: 1,
                                            background: '#eee',
                                            color: 'var(--color-default-text)',
                                            px: 1,
                                            py: 0.5
                                        }}
                                    >
                                        {quotedDate}
                                    </Box>
                                </Box>
                            )}
                            {acceptedDate && (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Accepted:</Typography>
                                    <Box
                                        sx={{ 
                                            fontSize: 12,
                                            borderRadius: 1,
                                            background: '#eee',
                                            color: 'var(--color-default-text)',
                                            px: 1,
                                            py: 0.5
                                        }}
                                    >
                                        {acceptedDate}
                                    </Box>
                                </Box>
                            )}
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Invoice:</Typography>
                                <Box
                                    ref={inputRef}
                                    onClick={(e) => {
                                        const initialDate = pickerValue ?? (data.issuedDate ? dayjs(data.issuedDate) : dayjs());
                                        setDraftDate(initialDate);
                                        setDraftConfidenceLevel(data.confidenceLevel ?? 100);
                                        resetProvisionDateDraft(initialDate);
                                        setProvisionAnchorEl(e.currentTarget);
                                    }}
                                    sx={{
                                        fontSize: 12,
                                        borderRadius: 1,
                                        border: '1px solid var(--color-primary)',
                                        color: 'var(--color-default-text)',
                                        px: 1,
                                        py: 0.5,
                                        cursor: 'pointer'
                                    }}
                                >
                                    { formatDate?.(data.issuedDate) }
                                </Box>
                                <Popover
                                    open={Boolean(provisionAnchorEl)}
                                    anchorEl={provisionAnchorEl}
                                    onClose={() => setProvisionAnchorEl(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                    PaperProps={{ sx: { p: 2, width: 260, borderRadius: 2 } }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', mb: 1 }}>Invoice Date</Typography>
                                    <DatePicker
                                        open={calendarOpen}
                                        onOpen={() => setCalendarOpen(true)}
                                        onClose={() => setCalendarOpen(false)}
                                        value={draftDate}
                                        onChange={(newValue: any) => {
                                            const date = dayjs.isDayjs(newValue) ? newValue : (newValue ? dayjs(newValue) : null);
                                            if (date && date.isValid()) setDraftDate(date);
                                        }}
                                        format={companySettings?.settings?.dateformat || 'DD-MM-YYYY'}
                                        enableAccessibleFieldDOMStructure={false}
                                        slotProps={{
                                            textField: { size: 'small', fullWidth: true },
                                            popper: { anchorEl: inputRef.current },
                                        }}
                                    />
                                    <Divider sx={{ my: 1.5 }} />
                                    {(() => {
                                        // A brand-new invoice with no client picked yet has nothing to base a
                                        // suggestion on - don't claim a client-specific (or even a settings-based)
                                        // recommendation until one is actually selected.
                                        const hasClient = isEdit || Boolean(data.clientId);
                                        const offsetDays = computeDefaultProvisionOffset();
                                        const todayFloor = dayjs().startOf('day');
                                        // The backend enforces the same "never in the past" floor at
                                        // generation time (see generateTransactionsFromInvoice), but a
                                        // recommendation that quietly suggests a past date and then gets
                                        // silently bumped forward later is a confusing mismatch - clamp
                                        // it here too so what's shown is what actually gets used.
                                        const rawRecommendedDate = draftDate ? draftDate.add(offsetDays, 'day') : null;
                                        const recommendedDate = rawRecommendedDate && rawRecommendedDate.isBefore(todayFloor)
                                            ? todayFloor
                                            : rawRecommendedDate;
                                        const isRecommended = provisionDateMode === 'recommended';
                                        const abs = Math.abs(offsetDays);
                                        // Only claim this reflects the CLIENT's own behavior when we actually have
                                        // a computed average for them; otherwise we're falling back to the entity's
                                        // Invoice Settings default, and the label should say so honestly. A manual
                                        // override (set in Client Details) takes priority over both and gets its
                                        // own honest label too - it was previously invisible here entirely, so the
                                        // note always claimed "no payment history" even when an admin had
                                        // explicitly set this client's terms.
                                        const isClientDerived = !hasManualClientOffset && paymentBehavior?.averageDaysToPayment != null;
                                        const label = !hasClient
                                            ? 'Select a client to see their suggested provision date.'
                                            : hasManualClientOffset
                                                ? `This client's provision terms were manually set to ${abs} day${abs !== 1 ? 's' : ''} after the Invoice Date.`
                                                : isClientDerived
                                                    ? (offsetDays >= 0
                                                        ? `This client normally pays within ${abs} day${abs !== 1 ? 's' : ''} after the Invoice Date.`
                                                        : `This client normally pays ${abs} day${abs !== 1 ? 's' : ''} before the Invoice Date.`)
                                                    : `No payment history yet - using the default provision terms of ${abs} day${abs !== 1 ? 's' : ''} after the Invoice Date.`;
                                        const color = !hasClient ? 'var(--color-text-muted)' : (offsetDays < 0 ? '#1b5e20' : '#1565c0');
                                        return (
                                            <>
                                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)', mb: 0.5 }}>Provision Date</Typography>
                                                {hasClient && (
                                                    <>
                                                        <RadioGroup
                                                            row
                                                            value={provisionDateMode}
                                                            onChange={(e) => {
                                                                const mode = e.target.value as 'recommended' | 'custom';
                                                                setProvisionDateMode(mode);
                                                                if (mode === 'custom' && !draftCustomProvisionDate) {
                                                                    setDraftCustomProvisionDate(recommendedDate);
                                                                }
                                                            }}
                                                            sx={{ mb: 0.5 }}
                                                        >
                                                            <FormControlLabel value="recommended" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 11 }}>Recommended</Typography>} />
                                                            <FormControlLabel value="custom" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 11 }}>Custom</Typography>} />
                                                        </RadioGroup>
                                                        <DatePicker
                                                            value={isRecommended ? recommendedDate : draftCustomProvisionDate}
                                                            onChange={(newValue: any) => {
                                                                const dateVal = dayjs.isDayjs(newValue) ? newValue : (newValue ? dayjs(newValue) : null);
                                                                if (dateVal && dateVal.isValid()) setDraftCustomProvisionDate(dateVal);
                                                            }}
                                                            disabled={isRecommended}
                                                            minDate={todayFloor}
                                                            format={companySettings?.settings?.dateformat || 'DD-MM-YYYY'}
                                                            enableAccessibleFieldDOMStructure={false}
                                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                                        />
                                                    </>
                                                )}
                                                <Typography sx={{ fontSize: 11, fontStyle: 'italic', color, mt: 0.5 }}>{label}</Typography>
                                                {isQuote && (
                                                    <>
                                                        <Divider sx={{ my: 1.5 }} />
                                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary)', mb: 0.5 }}>Confidence Level</Typography>
                                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--color-primary)', textAlign: 'center', background: 'rgba(var(--color-primary-rgb), 0.1255)', borderRadius: 1, py: 0.5, mb: 0.5 }}>
                                                            {formatAmount?.((data.grandTotal || 0) * (draftConfidenceLevel / 100))}
                                                        </Typography>
                                                        <Slider
                                                            value={draftConfidenceLevel}
                                                            onChange={(_, value) => setDraftConfidenceLevel(value as number)}
                                                            min={10}
                                                            max={100}
                                                            step={10}
                                                            marks
                                                            valueLabelDisplay="auto"
                                                            valueLabelFormat={(v) => `${v}%`}
                                                            sx={{ color: 'var(--color-primary)', mx: 0.5, width: 'calc(100% - 8px)' }}
                                                        />
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                    <Box display="flex" gap={1} mt={1.5}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            sx={{ flex: 1, background: 'var(--color-primary)', color: '#fff', fontWeight: 600, fontSize: 11, '&:hover': { background: '#009b84' } }}
                                            onClick={() => {
                                                if (draftDate && draftDate.isValid()) {
                                                    const isoDate = draftDate.format('YYYY-MM-DD');
                                                    // Always send an explicit offset (recommended or custom) rather
                                                    // than null - the backend otherwise independently recomputes
                                                    // its own value at generation time, which can silently diverge
                                                    // from whatever was actually shown here.
                                                    const resolvedOffset = (provisionDateMode === 'custom' && draftCustomProvisionDate?.isValid())
                                                        ? draftCustomProvisionDate.diff(draftDate, 'day')
                                                        : computeDefaultProvisionOffset();
                                                    const provisionUpdates: Partial<InvoiceData> = isQuote
                                                        ? { provisionDateOffset: resolvedOffset, confidenceLevel: draftConfidenceLevel }
                                                        : { provisionDateOffset: resolvedOffset };
                                                    onChange({ issuedDate: isoDate, ...provisionUpdates });
                                                    setPickerValue(draftDate);
                                                }
                                                setProvisionAnchorEl(null);
                                            }}
                                        >
                                            APPLY NOW
                                        </Button>
                                        <Button
                                            variant="text"
                                            size="small"
                                            sx={{ flex: 1, color: 'var(--color-text-muted)', fontWeight: 600, fontSize: 11 }}
                                            onClick={() => {
                                                setDraftDate(pickerValue);
                                                setDraftConfidenceLevel(data.confidenceLevel ?? 100);
                                                if (pickerValue) resetProvisionDateDraft(pickerValue);
                                                setProvisionAnchorEl(null);
                                            }}
                                        >
                                            CLEAR
                                        </Button>
                                    </Box>
                                </Popover>
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex" gap={1} mt={1}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Invoice Descriptor</Typography>
                        <TextField
                            value={data.invoiceDescriptor || ''}
                            onChange={(e) => onChange({ invoiceDescriptor: e.target.value })}
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            placeholder="Enter invoice descriptor..."
                            sx={{ background: 'var(--color-page-background)', borderRadius: 1, fontSize: 12 }}
                        />
                    </Box>
                </Box>
                <Box sx={{ flex: 1, width: isMobile ? '100%' : undefined }}>
                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 2, textAlign: isMobile ? 'left' : 'right' }}>
                        {lastEmailDate ? `* This invoice was last sent on ${formatDate(lastEmailDate)} to ${data.companyEmail}` : '* This invoice has not been sent yet'}
                    </Typography>
                    <Box 
                        sx={{ 
                            background: 'rgba(var(--color-primary-rgb), 0.1255)',
                            borderRadius: 2,
                            p: 1,
                            ...(isMobile ? { minHeight: 150 } : { height: 150 })
                        }}
                    >
                        <Box display="flex" gap={1} alignItems="center" mb={1}>
                            <Typography fontSize={12} color='var(--color-text-muted)' sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                Company <span style={{ color: '#F44336' }}>*</span>
                            </Typography>
                            <Autocomplete
                                disabled={isEdit}
                                options={clientList}
                                sx={{ width: '100%' }}
                                size='small'
                                PaperComponent={CustomPaper}
                                getOptionLabel={(option: any) => typeof option === 'string' ? option : (option.company || '')}
                                isOptionEqualToValue={(option: any, value: any) => {
                                    return typeof value !== 'string' && option.id === value.id;
                                }}
                                value={clientList.find(c => c.company === data.companyName) ?? null}
                                onChange={(_: any, value: any) => {
                                    if (!value) {
                                        onChange({ companyName: '', clientId: '' });
                                        return;
                                    }
                                    if (typeof value === 'string') {
                                        onChange({ companyName: value });
                                        return;
                                    }
                                    // when a client is selected, populate fields from that client
                                    onChange({
                                        clientId: String(value.id),
                                        invoiceNumber: `${value.abbr}${value.nextInvoiceNumber}`,
                                        companyName: value.company,
                                        recipientName: value.recipient,
                                        companyAddress: value.streetAddressLine1,
                                        companyAddress2: value.streetAddressLine2,
                                        companySuburb: value.suburb,
                                        companyState: value.state,
                                        companyPostalCode: value.postalCode,
                                        companyPhone: value.phone,
                                        companyFax: value.fax,
                                        companyEmail: value.email
                                    });
                                }}
                                renderOption={(props: any, option: any) => (
                                    <li {...props}>
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{option.company}</div>
                                            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{option.abbr} — {option.recipient}</div>
                                        </div>
                                    </li>
                                    )}
                                    renderInput={(params: any) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            fullWidth
                                            error={!!errors.companyName}
                                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                                        />
                                    )}
                                />
                        </Box>
                        {data.companyName && (
                            <Box>
                                <Box display="flex" flexDirection="row" alignItems="flex-start" gap={0.5}>
                                    <Box width={50} display="flex" justifyContent="flex-end">
                                        <IconButton
                                            onClick={onEditClientClick}
                                        >
                                            <Edit size={16} color="var(--color-primary)" />
                                        </IconButton>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: 'var(--color-default-text)' }}>
                                            {data.recipientName}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                            {data.companyAddress}
                                        </Typography>
                                        {data.companyAddress2 && (
                                            <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                                {data.companyAddress2}
                                            </Typography>
                                        )}
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                            {data.companySuburb}, {data.companyState} {data.companyPostalCode}
                                        </Typography>
                                        <Box display="flex" gap={2} flexWrap="wrap">
                                            {data.companyPhone && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                                    <span style={{ fontWeight: 500 }}>P:</span> {data.companyPhone}
                                                </Typography>
                                            )}
                                            {data.companyFax && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                                    <span style={{ fontWeight: 500 }}>F:</span> {data.companyFax}
                                                </Typography>
                                            )}
                                            {data.companyEmail && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                                    <span style={{ fontWeight: 500 }}>E:</span> {data.companyEmail}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
        <AddClientPopup
            open={openAddClient}
            onClose={() => setOpenAddClient(false)}
            onCreate={handleCreateClient}
        />
        </>
    );
};

export default InvoiceHeader;